import { useForm, Controller } from 'react-hook-form';
import { Typography } from '@mui/material';
import { Close } from '@mui/icons-material';

import './UserForm.css';
import { UserFormData, UserFormProps } from './UserForm.types';

const roles = [
  { value: 'Administrator', label: 'Адміністратор' },
  { value: 'Manager', label: 'Менеджер' },
  { value: 'Marketer', label: 'Маркетолог' },
  { value: 'User', label: 'Користувач' }
];

/**
 * Form component for creating and editing users
 * Displays different fields based on selected role (phone number for Administrator)
 * @param {UserFormProps} props - Component props
 * @returns {React.ReactElement} The rendered user form
 */
export function UserForm({ editingUser, isLoading, onSubmit, onCancel }: UserFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    trigger,
    setValue
  } = useForm<UserFormData>({
    mode: 'onChange',
    defaultValues: {
      email: editingUser?.email || '',
      display_name: editingUser?.display_name || '',
      role: editingUser?.role || 'Manager',
      is_active: editingUser?.is_active ?? true,
      notify: editingUser?.role === 'Administrator' ? (editingUser?.notify ?? false) : false,
      phone_number: editingUser?.phone_number || ''
    }
  });

  const selectedRole = watch('role');

  return (
    <div className="user-form">
      <div className="user-form-header">
        <Typography className="user-form-title">
          {editingUser ? 'Редагувати користувача' : 'Створити нового користувача'}
        </Typography>
        <button onClick={onCancel} className="user-form-close-button">
          <Close />
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="user-form-fields">
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Електронна пошта обов'язкова",
              validate: (value: string) => {
                const trimmed = value.trim();
                if (trimmed !== value) return 'Електронна пошта не повинна містити пробіли';
                if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(trimmed))
                  return 'Неправильний формат електронної пошти';
                return true;
              }
            }}
            render={({ field }) => (
              <div className="user-form-field">
                <div className="user-form-field-label">Електронна пошта</div>
                <div className="user-form-input-wrapper">
                  <input
                    {...field}
                    type="email"
                    placeholder="user@company.com"
                    disabled={!!editingUser}
                    className={`user-form-input ${errors.email ? 'error' : ''} ${editingUser ? 'disabled' : ''}`}
                  />
                  {errors.email && <div className="user-form-error-message">{errors.email.message}</div>}
                </div>
              </div>
            )}
          />

          <Controller
            name="display_name"
            control={control}
            rules={{ required: "Ім'я для відображення обов'язкове" }}
            render={({ field }) => (
              <div className="user-form-field">
                <div className="user-form-field-label">Ім'я для відображення</div>
                <div className="user-form-input-wrapper">
                  <input
                    {...field}
                    placeholder="Іван Петренко"
                    className={`user-form-input ${errors.display_name ? 'error' : ''}`}
                  />
                  {errors.display_name && <div className="user-form-error-message">{errors.display_name.message}</div>}
                </div>
              </div>
            )}
          />

          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <div className="user-form-field role-field">
                <div className="user-form-field-label">Роль</div>
                <select
                  {...field}
                  className="user-form-select"
                  onChange={e => {
                    field.onChange(e);
                    trigger('phone_number');
                    if (e.target.value !== 'Administrator') {
                      setValue('notify', false);
                    }
                  }}
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          />

          {selectedRole === 'Administrator' && (
            <Controller
              name="phone_number"
              control={control}
              rules={{
                validate: (value?: string) => {
                  if (!value) return true;
                  if (!/^380\d{9}$/.test(value)) return 'Номер повинен починатися з 380 і містити 12 цифр';
                  return true;
                }
              }}
              render={({ field }) => (
                <div className="user-form-field">
                  <div className="user-form-field-label">Номер телефону</div>
                  <div className="user-form-input-wrapper">
                    <input
                      {...field}
                      type="tel"
                      placeholder="380*********"
                      maxLength={12}
                      onInput={e => {
                        const target = e.target as HTMLInputElement;
                        target.value = target.value.replace(/\D/g, '');
                        field.onChange(target.value);
                      }}
                      className={`user-form-input ${errors.phone_number ? 'error' : ''}`}
                    />
                    {errors.phone_number && (
                      <div className="user-form-error-message">{errors.phone_number.message}</div>
                    )}
                  </div>
                </div>
              )}
            />
          )}

          <div className="user-form-toggles-row">
            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <div className="user-form-field toggle-field">
                  <div className="user-form-toggle-switch">
                    <div
                      className={`user-form-toggle-button ${field.value ? 'active' : 'inactive'}`}
                      onClick={() => field.onChange(!field.value)}
                    >
                      <div className={`user-form-toggle-slider ${field.value ? 'active' : 'inactive'}`} />
                    </div>
                    <div className="user-form-toggle-label">Активний</div>
                  </div>
                </div>
              )}
            />

            {selectedRole === 'Administrator' && (
              <Controller
                name="notify"
                control={control}
                render={({ field }) => (
                  <div className="user-form-field notification-field">
                    <div className="user-form-toggle-switch">
                      <div
                        className={`user-form-toggle-button ${field.value ? 'active' : 'inactive'}`}
                        onClick={() => field.onChange(!field.value)}
                      >
                        <div className={`user-form-toggle-slider ${field.value ? 'active' : 'inactive'}`} />
                      </div>
                      <div className="user-form-toggle-label">Сповіщення</div>
                    </div>
                  </div>
                )}
              />
            )}
          </div>
        </div>

        <div className="user-form-actions">
          <button type="button" onClick={onCancel} className="user-form-cancel-button">
            Скасувати
          </button>
          <button
            type="submit"
            disabled={!isValid || isLoading}
            className={`user-form-submit-button ${!isValid || isLoading ? 'disabled' : ''}`}
            title={!isValid ? 'Виправте помилки валідації' : ''}
          >
            {editingUser ? 'Оновити' : 'Створити'} користувача
          </button>
        </div>
      </form>
    </div>
  );
}
